/**
 * Pure helpers for the enrolled usage log (credit metering Step 10). The log reads the daily
 * usage API: accrued usage is what is shown as used, posted debits are shown separately, and a
 * set-aside (reserved) amount is never part of this response nor shown as spent.
 */
import { assistantLevelRateExact, type ConversationLevel } from '@digital-twins/shared-types'

import { formatMillicredits } from '@/lib/billing/metering'

import type { DailyUsageChild, DailyUsageChildType, DailyUsageRow } from '@/lib/api'

/** Credits shown for an exact amount: `<0.01` for a tiny positive, never rounded to zero. */
export const formatUsageCredits = formatMillicredits

/** Like `formatUsageCredits`, but a refund keeps its sign (`-180`). */
export function formatSignedUsageCredits(millicredits: number): string {
  return millicredits < 0 ? `-${formatMillicredits(-millicredits)}` : formatMillicredits(millicredits)
}

/** A row that gives credits back (a refund on its own day), not one that spends them. */
export function isRefundRow(row: DailyUsageRow): boolean {
  return row.accruedMillicredits < 0 || row.postedCredits < 0
}

/**
 * The extras of a row: charges beside its own. A thread's are what its replies launched
 * (images, Quick Studies, studies, rooms, workflow runs); a room's are messages beyond the
 * plain charge (a new-video 2x message). Studies, Quick Studies, refunds and other rows
 * have none. The replies or messages themselves are the row's first line, never listed.
 */
export function rowExtras(row: DailyUsageRow): Array<DailyUsageChild> {
  if (row.kind === 'thread') {
    return row.children.filter((child) => child.type !== 'assistant')
  }
  if (row.kind === 'room') {
    return row.children.filter((child) => child.type !== 'room_message' || child.newVideo)
  }
  if (row.kind === 'workflow') {
    return row.children.filter((child) => child.type !== 'workflow')
  }
  return []
}

/**
 * A row expands only to list its extras, and not when its one charge is the extra itself
 * (an image-only thread day would repeat the row), unless that carries a new-video tag.
 */
export function rowHasDetail(row: DailyUsageRow): boolean {
  const extras = rowExtras(row)
  return extras.length > 0 && (row.children.length > 1 || extras.some((child) => child.newVideo))
}

const CHILD_ORDER: Record<DailyUsageChildType, number> = {
  assistant: 0,
  workflow: 0.5,
  image: 1,
  quick_study: 2,
  study: 3,
  room_message: 4,
  social: 5,
  audience: 6,
  chat: 7,
  other: 8,
}

/** Children nested under a row: Assistant usage first, then by type, then by time. */
export function orderUsageChildren(children: ReadonlyArray<DailyUsageChild>): Array<DailyUsageChild> {
  return [...children].sort((a, b) => {
    const byType = (CHILD_ORDER[a.type] ?? 99) - (CHILD_ORDER[b.type] ?? 99)
    if (byType !== 0) {
      return byType
    }
    return a.occurredAt.localeCompare(b.occurredAt)
  })
}

/** A usage row with its children in display order. */
export function nestUsageRow(row: DailyUsageRow): DailyUsageRow {
  return { ...row, children: orderUsageChildren(row.children) }
}

const LEVELS: ReadonlyArray<string> = ['low', 'medium', 'high']

/**
 * Weighted tokens of an Assistant or workflow charge: input, cached input and output at the
 * price's own weights (1 : 0.1 : 5 today, read from the pricing table of the charge's
 * version). Only the total is ever shown, never the parts. Null for other charges.
 */
export function weightedTokens(child: DailyUsageChild): number | null {
  if ((child.type !== 'assistant' && child.type !== 'workflow') || child.link || !child.tokens) {
    return null
  }
  const level = (child.level && LEVELS.includes(child.level) ? child.level : 'medium') as ConversationLevel
  let rate
  try {
    rate = assistantLevelRateExact(level, child.policyVersion ?? undefined)
  } catch {
    // A version this build does not know: the current weights.
    rate = assistantLevelRateExact(level)
  }
  const weight = (perMillion: number) => perMillion / rate.inputCreditsPerMillion
  const { inputTokens, cachedInputTokens, outputTokens } = child.tokens
  return (
    inputTokens +
    cachedInputTokens * weight(rate.cachedInputCreditsPerMillion) +
    outputTokens * weight(rate.outputCreditsPerMillion)
  )
}

/** A row's weighted tokens over its Assistant and workflow charges, or null when it has none. */
export function rowWeightedTokens(row: DailyUsageRow): number | null {
  const values = row.children.map(weightedTokens).filter((value): value is number => value !== null)
  return values.length ? values.reduce((sum, value) => sum + value, 0) : null
}

/** `950`, `95.4k`, `1.2M`: one decimal from a thousand up. */
export function formatWeightedTokens(value: number, locale?: string): string {
  const oneDecimal = (n: number) => n.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  if (value < 1000) {
    return Math.round(value).toLocaleString(locale)
  }
  if (value < 1_000_000) {
    return `${oneDecimal(value / 1000)}k`
  }
  return `${oneDecimal(value / 1_000_000)}M`
}
